// src/pages/home/productCardsSection.jsx
import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import MoreDeskDelightsSection from "./MoreDeskDelightsSection";

const API_BASE = "https://aishcraft.com/backend/api";

const ProductCard = ({ product }) => {
  const defaultColor = product.swatches[0] || "default";
  const [activeColor, setActiveColor] = useState(defaultColor);
  const [fade, setFade] = useState(false);

  const currentImage = product.images[activeColor] || product.images["default"];

  const handleSwatchClick = (e, color) => {
    e.preventDefault();
    if (color === activeColor) return;
    setFade(true);
    setTimeout(() => {
      setActiveColor(color);
      setFade(false);
    }, 150);
  };

  return (
    <Link
      to={product.href}
      className="flex flex-col items-center group cursor-pointer active:scale-[0.97] transition-transform duration-150"
    >
      <div className="w-full flex items-end justify-center mt-1 sm:mt-10 mb-2 sm:mb-6 h-[150px] xs:h-[175px] sm:h-[200px] md:h-[240px] px-1 sm:px-2">
        <img
          src={currentImage}
          alt={product.name}
          loading="lazy"
          className="max-h-full max-w-full object-contain transition-all duration-300 group-hover:-translate-y-1 group-hover:scale-105"
          style={{
            opacity: fade ? 0 : 1,
            filter: "drop-shadow(0px 15px 12px rgba(0,0,0,0.15))",
          }}
        />
      </div>

      <p className="text-[13px] xs:text-[14px] sm:text-[14px] md:text-[15px] font-bold text-[#111] text-center font-body mb-0.5 sm:mb-1 px-1 leading-snug line-clamp-2 min-h-[28px] xs:min-h-[32px] sm:min-h-0">
        {product.name}
      </p>
      {product.type && (
        <p className="text-[11px] xs:text-[12px] sm:text-[12px] md:text-[13px] font-medium text-gray-500 text-center font-body mb-1 xs:mb-1.5 sm:mb-2.5">
          {product.type}
        </p>
      )}

      <div className="flex items-center gap-1 sm:gap-2 justify-center mb-1.5 xs:mb-2 sm:mb-3.5 flex-wrap">
        {product.newPrice && (
          <span className="text-[13px] xs:text-[14px] sm:text-[14px] font-bold text-[#111] font-body">
            {product.newPrice}
          </span>
        )}
        {product.oldPrice && (
          <span className="text-[10.5px] xs:text-[12px] sm:text-[12px] font-bold text-[#111] font-body bg-[#fce954] px-1 rounded-sm line-through decoration-black/70">
            {product.oldPrice}
          </span>
        )}
      </div>

      {product.writeText ? (
        <p className="text-[10.5px] xs:text-[12px] sm:text-[13px] font-bold text-[#d93a49] text-center font-body">
          {product.writeText}
        </p>
      ) : (
        product.swatches.length > 0 && (
          <div className="flex gap-1.5 sm:gap-2 justify-center flex-wrap">
            {product.swatches.map((color) => (
              <button
                key={color}
                onClick={(e) => handleSwatchClick(e, color)}
                title={color}
                className="w-[20px] h-[20px] sm:w-[18px] sm:h-[18px] rounded-full transition-all duration-200 flex-shrink-0"
                style={{
                  background: color,
                  border: activeColor === color ? "2px solid #111" : "1px solid #d1d5db",
                }}
                aria-label={`Select color ${color}`}
              />
            ))}
          </div>
        )
      )}
    </Link>
  );
};

const ProductCardsSection = ({
  title = "Daily journals & diary books from the heart",
  linkText = "Find yours",
  linkHref = "/Dailyplanner",
}) => {
  // Ab koi static fallback nahi — sirf backend se products aate hain.
  // Jab tak backend se data nahi aata, list empty rehti hai (grid simply khali dikhega,
  // crash nahi hoga).
  const [products, setProducts] = useState([]);

  useEffect(() => {
    fetch(`${API_BASE}/home/fatherday.php`)
      .then(res => res.json())
      .then(json => {
        if (json.success && Array.isArray(json.data) && json.data.length > 0) {
          const mapped = json.data
            .filter(p => p.image)
            .map((p) => ({
              id: p.id,                              // ← real DB id use karo
              name: p.name,
              type: p.subtitle || "",
              newPrice: p.newPrice ? `Rs. ${p.newPrice}` : "",
              oldPrice: p.oldPrice ? `Rs. ${p.oldPrice}` : "",
              writeText: null,
              swatches: [],
              images: { default: p.image },
              // Goes to the real product page if admin linked one;
              // otherwise gets its own automatic product page.
              href: p.productHref || `/fatherday/${p.id}`,   // ← fallback fix
            }));
          if (mapped.length > 0) setProducts(mapped);
        }
      })
      .catch(() => {
        // Backend fail ho to bhi crash nahi hoga — list bas empty rahegi
      });
  }, [linkHref]);

  return (
    <section className="bg-white py-6 sm:py-10 px-3 sm:px-5 md:px-10 font-body">
      <div className="max-w-[1300px] mx-auto">
        <div className="flex items-start xs:items-center justify-between mb-4 xs:mb-5 sm:mb-2 gap-2">
          <h2 className="font-serif italic text-[16px] xs:text-[19px] sm:text-2xl md:text-[34px] text-[#111] leading-[1.2] xs:leading-[1.15] pr-2">
            {title}
          </h2>
          <Link
            to={linkHref}
            className="text-[11px] xs:text-[12.5px] sm:text-[14px] font-bold text-[#111] underline-offset-4 whitespace-nowrap flex items-center gap-1 flex-shrink-0 mt-1 xs:mt-0"
          >
            {linkText} <span className="text-lg leading-none">›</span>
          </Link>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2.5 xs:gap-3.5 sm:gap-6 gap-y-3 xs:gap-y-4 sm:gap-y-12">
          {products.map((product) => (
            <ProductCard key={product.id} product={product} />
          ))}
        </div>

        <MoreDeskDelightsSection onCategoryClick={(cat) => console.log(cat)} />
      </div>
    </section>
  );
};

export default ProductCardsSection;